// =============================================================================
// ESPOLIDAR WEB ADMIN - MAIN APP
// =============================================================================

import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import DevicesPage from './pages/DevicesPage'
import DeviceDetailPage from './pages/DeviceDetailPage'
import MediaPage from './pages/MediaPage'
import ResellersPage from './pages/ResellersPage'
import ResellerDetailPage from './pages/ResellerDetailPage'
import UsersPage from './pages/UsersPage'
import EventsPage from './pages/EventsPage'
import StatisticsPage from './pages/StatisticsPage'
import AnalysisPage from './pages/AnalysisPage'
import OTAPage from './pages/OTAPage'
import LoadingSpinner from './components/LoadingSpinner'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Solo admin e superadmin possono accedere
  if (user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Solo superadmin può accedere
  if (user.role !== 'superadmin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="devices" element={<AdminRoute><DevicesPage /></AdminRoute>} />
        <Route path="devices/:deviceId" element={<AdminRoute><DeviceDetailPage /></AdminRoute>} />
        <Route path="media" element={<AdminRoute><MediaPage /></AdminRoute>} />
        <Route path="resellers" element={<SuperAdminRoute><ResellersPage /></SuperAdminRoute>} />
        <Route path="resellers/:id" element={<SuperAdminRoute><ResellerDetailPage /></SuperAdminRoute>} />
        <Route path="users" element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />
        <Route path="events" element={<EventsPage />} />
        <Route path="statistics" element={<StatisticsPage />} />
        <Route path="analysis" element={<AnalysisPage />} />
        <Route path="ota" element={<AdminRoute><OTAPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
